import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import API_BASE from "../config/api";
import { getInitials, yearsSince, resolveImageUrl } from "../utils/siteHelpers";
import SiteLoadingState from "../components/site/SiteLoadingState";
import SiteErrorState from "../components/site/SiteErrorState";
import SiteEmptyState from "../components/site/SiteEmptyState";
import { getThemeVars } from "../utils/businessThemes";

/* Pulls the 11-char video id out of any common YouTube URL shape
   (watch?v=, youtu.be/, /embed/, /shorts/). Returns "" if it doesn't match. */
function extractYouTubeId(url) {
    if (!url) return "";
    const m = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
    return m ? m[1] : "";
}

/* BLANK defaults — a newly activated site shows its OWN (empty) content, not demo data. */
const DEMO = {
    name: "",
    contact_person: "",
    tagline: "",
    logo: "",
    cover: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    about: "",
    nature_of_business: "",
    year_established: "",
    gst_no: "",
    legal_status: "",
    employees: "",
    annual_turnover: "",
    linkfacebook: "",
    linkinstagram: "",
    linklinkedin: "",
    products: [],
    gallery: [],
    videos: [],
};

const REVIEWS = [];

/* Generic, business-agnostic FAQ. Doesn't depend on any new backend field,
   so it works for every listing without a schema change. */
const FAQS = [
    { q: "How quickly do you respond to enquiries?", a: "Most enquiries submitted through this page are answered within one business day." },
    { q: "Can I get a custom quote?", a: "Yes — use the \"Send Enquiry\" button on any product or the contact form below and we'll get back with pricing." },
    { q: "Do you offer support after purchase?", a: "Yes, our team is available for after-sales support — reach out using the contact details in the footer." },
];

/* Accessible modal overlay: closes on Escape, on backdrop click, traps nothing
   fancy (keeps things simple) but does return focus to the trigger on close. */
function useEscapeToClose(isOpen, onClose) {
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);
}

/* Tracks header scroll position so the nav can pick up a stronger shadow
   once the page has scrolled — small production-feel detail. */
function useScrolled(threshold = 12) {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > threshold);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [threshold]);
    return scrolled;
}

/* Drop-in <img> replacement: if `src` is empty or fails to load, renders a
   themed animated-gradient tile with a centered icon instead of a broken
   image box or the browser's default alt-text placeholder. Resets its own
   "ok" state whenever the src changes so a previous failure doesn't stick. */
function BwImg({ src, alt, className = "", icon = "bi-image", loading = "lazy" }) {
    const [ok, setOk] = useState(true);
    useEffect(() => { setOk(true); }, [src]);
    if (src && ok) {
        return <img src={src} alt={alt} loading={loading} className={className} onError={() => setOk(false)} />;
    }
    return (
        <div className={`bw-img-fallback ${className}`} role="img" aria-label={alt || "Image unavailable"}>
            <i className={`bi ${icon}`} aria-hidden="true" />
        </div>
    );
}

export default function BusinessWebsiteProfessionalTemplate() {
    const { domain } = useParams();
    const [real, setReal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [fetchToken, setFetchToken] = useState(0);

    const [tab, setTab] = useState("home");
    const [enq, setEnq] = useState({ open: false, product: "", name: "", mobile: "", email: "", message: "" });
    const [enqMsg, setEnqMsg] = useState(null);
    const [video, setVideo] = useState(null);
    const [detail, setDetail] = useState(null); // product detail popup
    const [openFaq, setOpenFaq] = useState(0);
    const [coverOk, setCoverOk] = useState(true);
    const [logoOk, setLogoOk] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const sliderRef = useRef(null);
    const scrolled = useScrolled();

    useEffect(() => {
        if (!domain) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setLoadError(false);
        fetch(`${API_BASE}/api/get_business_site.php?domain=${encodeURIComponent(domain)}`)
            .then((r) => r.json())
            .then((res) => { if (!cancelled && res.success && res.data) setReal(res.data); })
            .catch(() => { if (!cancelled) setLoadError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [domain, fetchToken]);

    const pick = (k) => (real && real[k] && String(real[k]).trim() !== "") ? real[k] : DEMO[k];
    // API image fields (logo, cover, gallery photos, product photos) come back as paths
    // relative to the PHP backend root (e.g. "uploads/business/gallery/xxx.jpg"), not
    // full URLs — same as brochure_file. Every one of them needs this before rendering,
    // or the browser resolves them against the frontend's own origin and 404s.
    const resolveImg = (v) => (v ? resolveImageUrl(API_BASE, v) : v);
    const d = {
        name: pick("name"), contact_person: (real && real.name) ? real.name : DEMO.contact_person,
        tagline: pick("tagline"), cover: resolveImg(real ? (real.cover || "") : DEMO.cover),
        logo: resolveImg(pick("logo")), address: pick("address"), phone: pick("phone"), email: pick("email"),
        website: pick("website"), about: pick("about"), nature_of_business: pick("nature_of_business"),
        year_established: pick("year_established"), gst_no: pick("gst_no"), legal_status: pick("legal_status"),
        employees: pick("employees"), annual_turnover: pick("annual_turnover"),
        linkfacebook: pick("linkfacebook"), linkinstagram: pick("linkinstagram"), linklinkedin: pick("linklinkedin"),
        // CR B5 / B6 / B7 / B8 / B10
        youtube_link: (real && real.youtube_link) || "",
        brochure_file: (real && real.brochure_file) || "",
        brochure_link: (real && real.brochure_link) || "",
        theme_color: (real && real.theme_color) || "",
        is_verified: !!(real && Number(real.is_verified) === 1),
        rating: real && real.rating !== null && real.rating !== undefined ? Number(real.rating) : null,
        review_count: (real && real.review_count) || 0,
    };
    const products = (real && real.products && real.products.length) ? real.products : DEMO.products;
    const gallery = (real && real.gallery && real.gallery.length) ? real.gallery : DEMO.gallery;
    const ytId = extractYouTubeId(d.youtube_link);
    const videos = ytId ? [{ youtubeId: ytId, title: d.name || "Business video" }] : DEMO.videos;
    const brochureUrl = d.brochure_file ? resolveImageUrl(API_BASE, d.brochure_file) : (d.brochure_link || "");
    const theme = getThemeVars(d.theme_color);
    const isSiteEmpty = !d.name && !d.about && products.length === 0 && gallery.length === 0;

    // Live product/service search — matches name, category, and description.
    // Clearing the box (or an empty query) always falls back to the full list.
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filteredProducts = useMemo(() => {
        if (!normalizedQuery) return products;
        return products.filter((p) => {
            const haystack = `${p.name || ""} ${p.category || ""} ${p.description || ""}`.toLowerCase();
            return haystack.includes(normalizedQuery);
        });
    }, [products, normalizedQuery]);
    const isSearching = normalizedQuery.length > 0;
    const hasSearchResults = filteredProducts.length > 0;

    const grouped = useMemo(() => {
        const g = {}; filteredProducts.forEach((p) => { const c = (p.category || "General").trim() || "General"; (g[c] = g[c] || []).push(p); }); return g;
    }, [filteredProducts]);
    const categories = Object.keys(grouped);

    // Cover is a CSS background-image, which has no onError — so we preload
    // it in JS and only use it once we know it actually resolves. Same idea
    // for the logo, kept separate from the <img onError> pattern used below
    // since we need to swap it for a completely different element (initials box).
    useEffect(() => {
        if (!d.cover) { setCoverOk(false); return; }
        let cancelled = false;
        const img = new Image();
        img.onload = () => { if (!cancelled) setCoverOk(true); };
        img.onerror = () => { if (!cancelled) setCoverOk(false); };
        img.src = d.cover;
        return () => { cancelled = true; };
    }, [d.cover]);

    useEffect(() => {
        if (!d.logo) { setLogoOk(false); return; }
        let cancelled = false;
        const img = new Image();
        img.onload = () => { if (!cancelled) setLogoOk(true); };
        img.onerror = () => { if (!cancelled) setLogoOk(false); };
        img.src = d.logo;
        return () => { cancelled = true; };
    }, [d.logo]);

    // auto-scroll featured slider
    useEffect(() => {
        const el = sliderRef.current; if (!el) return;
        const id = setInterval(() => {
            if (!el) return;
            if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 5) el.scrollTo({ left: 0, behavior: "smooth" });
            else el.scrollBy({ left: 240, behavior: "smooth" });
        }, 3000);
        return () => clearInterval(id);
    }, [products]);

    const scrollTo = useCallback((id, key) => {
        setTab(key);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    // Typing in the search box should jump the page to the products section
    // (only once, right as a search starts) so results are visible immediately.
    const hasAutoScrolledForSearch = useRef(false);
    useEffect(() => {
        if (isSearching && !hasAutoScrolledForSearch.current) {
            hasAutoScrolledForSearch.current = true;
            scrollTo("products", "products");
        }
        if (!isSearching) hasAutoScrolledForSearch.current = false;
    }, [isSearching, scrollTo]);

    const clearSearch = useCallback(() => setSearchQuery(""), []);

    const openEnquiry = useCallback((p = "") => {
        setEnqMsg(null);
        setEnq({ open: true, product: p, name: "", mobile: "", email: "", message: p ? `I am interested in "${p}". Please share best price and details.` : "" });
    }, []);
    const closeEnquiry = useCallback(() => setEnq((s) => ({ ...s, open: false })), []);
    const closeVideo = useCallback(() => setVideo(null), []);
    const closeDetail = useCallback(() => setDetail(null), []);
    const openProduct = (p) => { if (p) setDetail(p); };

    useEscapeToClose(enq.open, closeEnquiry);
    useEscapeToClose(!!video, closeVideo);
    useEscapeToClose(!!detail, closeDetail);

    const submitEnquiry = async (e) => {
        e.preventDefault(); setEnqMsg(null);
        try {
            const res = await fetch(`${API_BASE}/api/save_business_enquiry.php`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ domain: domain || "demo", product: enq.product, name: enq.name, mobile: enq.mobile, email: enq.email, message: enq.message }),
            });
            const r = await res.json();
            if (r.success) { setEnqMsg({ type: "success", text: "Enquiry sent! The supplier will contact you soon." }); setTimeout(() => setEnq((s) => ({ ...s, open: false })), 1600); }
            else setEnqMsg({ type: "danger", text: r.message || "Could not send." });
        } catch { setEnqMsg({ type: "danger", text: "Network error." }); }
    };

    if (loading) return <SiteLoadingState theme="business" label="Loading business site…" />;
    if (loadError) return <SiteErrorState theme="business" onRetry={() => setFetchToken((t) => t + 1)} />;

    const initials = getInitials(d.name, "CN");
    const years = yearsSince(d.year_established);
    const Stars = ({ n }) => <span className="bw-stars" aria-hidden="true">{"★".repeat(n)}{"☆".repeat(5 - n)}</span>;

    const facts = [
        ["Nature of Business", d.nature_of_business, "bi-building"],
        ["Legal Status of Firm", d.legal_status, "bi-bank2"],
        ["No. of Employees", d.employees, "bi-people"],
        ["Annual Turnover", d.annual_turnover, "bi-graph-up-arrow"],
        ["Year of Establishment", d.year_established, "bi-calendar3"],
        ["VAT/PAN Number", d.gst_no, "bi-receipt"],
    ].filter((f) => f[1]);

    const whyUs = [
        ["bi-patch-check-fill", "Quality Assured", "Genuine, quality-checked products sourced from trusted brands."],
        ["bi-award-fill", "Trusted Supplier", `${years ? years + "+ years" : "Years"} of experience serving satisfied customers.`],
        ["bi-lightning-charge-fill", "Quick Response", "Fast quotations and responsive customer support."],
        ["bi-truck", "Reliable Service", "On-time delivery and dependable after-sales support."],
    ];

    // Nav items: only show tabs whose section actually has content.
    // Home and Contact Us always exist (hero + footer are always rendered).
    const navItems = [
        ["home", "Home", true],
        ["about", "About Us", !!d.about],
        ["products", "Products & Services", products.length > 0],
        ["videos", "Videos", videos.length > 0],
        ["photos", "Photos", gallery.length > 0],
        ["contact", "Contact Us", true],
    ].filter(([, , show]) => show);

    return (
        <div
            className="bw"
            style={{
                // CR B8: theme colour selection — overrides the defaults set in the
                // .bw class below via CSS variables (inline style wins on specificity).
                "--brand": theme.brand, "--brand-2": theme.brand2,
                "--accent": theme.accent, "--accent-d": theme.accentD,
            }}
        >
            <a href="#bw-main-content" className="bw-skip-link">Skip to content</a>

            {/* HEADER */}
            <header id="home" className="bw-head">
                <div className="bw-head-inner">
                    {(!d.logo || !logoOk) && <div className="bw-logobox" aria-hidden="true">{initials}</div>}
                    <div className="bw-head-main">
                        <h1 className="bw-cname">{d.name || "Your Business Name"}</h1>
                        {/* CR B10: category data display order — GST, Verified, then Rating */}
                        <div className="bw-badges">
                            {d.address && <span className="bw-loc"><i className="bi bi-geo-alt-fill" aria-hidden="true" /> {d.address}</span>}
                            {d.gst_no && <span className="bw-chip"><i className="bi bi-receipt" aria-hidden="true" /> PAN/VAT: {d.gst_no}</span>}
                            {d.is_verified && <span className="bw-chip ok"><i className="bi bi-patch-check-fill" aria-hidden="true" /> Verified Business</span>}
                            {d.rating !== null && (
                                <span className="bw-chip">
                                    <i className="bi bi-star-fill" aria-hidden="true" /> {d.rating.toFixed(1)}
                                    {d.review_count > 0 && ` (${d.review_count})`}
                                </span>
                            )}
                            {years !== null && <span className="bw-chip"><i className="bi bi-award" aria-hidden="true" /> {years} yrs experience</span>}
                        </div>
                    </div>
                    <div className="bw-head-cta">
                        <a className="bw-btn bw-btn-outline" href={d.phone ? `tel:${d.phone}` : "#"}><i className="bi bi-telephone-fill" aria-hidden="true" /> Call Now</a>
                        <button type="button" className="bw-btn bw-btn-primary" onClick={() => openEnquiry("")}><i className="bi bi-send-fill" aria-hidden="true" /> Contact Supplier</button>
                    </div>
                </div>
            </header>

            {/* NAV */}
            <nav className={`bw-nav ${scrolled ? "bw-nav-scrolled" : ""}`} aria-label="Site sections">
                <div className="bw-nav-inner">
                    {navItems.map(([k, label]) => (
                        <button
                            type="button"
                            key={k}
                            className={`bw-navtab ${tab === k ? "active" : ""}`}
                            aria-current={tab === k ? "true" : undefined}
                            onClick={() => scrollTo(k === "home" ? "home" : k, k)}
                        >
                            {label}
                        </button>
                    ))}
                    <form
                        className="bw-nav-search"
                        role="search"
                        onSubmit={(e) => { e.preventDefault(); scrollTo("products", "products"); }}
                    >
                        <i className="bi bi-search" aria-hidden="true" />
                        <label htmlFor="bw-search" className="visually-hidden-bw">Search products or services</label>
                        <input
                            id="bw-search"
                            type="search"
                            placeholder="Search Products / Services"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button type="button" className="bw-search-clear" onClick={clearSearch} aria-label="Clear search">
                                <i className="bi bi-x-lg" aria-hidden="true" />
                            </button>
                        )}
                    </form>
                </div>
            </nav>

            <main id="bw-main-content">
                {/* HERO */}
                <section
                    className={`bw-hero ${coverOk && d.cover ? "" : "bw-hero-fallback"}`}
                    style={coverOk && d.cover ? { backgroundImage: `linear-gradient(115deg, rgba(10,18,15,.90) 0%, rgba(15,26,20,.62) 50%, rgba(15,26,20,.30) 100%), url(${d.cover})` } : undefined}
                >
                    <div className="bw-hero-noise" aria-hidden="true"></div>
                    <div className="bw-hero-blob bw-hero-blob-1" aria-hidden="true"></div>
                    <div className="bw-hero-blob bw-hero-blob-2" aria-hidden="true"></div>
                    {(!coverOk || !d.cover) && (
                        <span className="bw-hero-watermark" aria-hidden="true">{initials}</span>
                    )}
                    <div className="bw-hero-in">
                        <div className="bw-hero-text">
                            <span className="bw-hero-eyebrow"><i className="bi bi-stars" aria-hidden="true"></i> {d.nature_of_business || "Welcome"}</span>
                            <h2 className="bw-hero-title">
                                {d.tagline || d.name || "Your Business Website"}
                                <span className="bw-hero-title-underline" aria-hidden="true"></span>
                            </h2>
                            <p className="bw-hero-desc">
                                {d.about && d.about.length > 190 ? d.about.slice(0, 190).trim() + "…" : (d.about || "Trusted, verified and ready to help — reach out any time.")}
                            </p>
                            <div className="bw-hero-cta">
                                <button type="button" className="bw-btn bw-btn-accent" onClick={() => scrollTo("products", "products")}>
                                    Explore Products <i className="bi bi-arrow-right" aria-hidden="true"></i>
                                </button>
                                <button type="button" className="bw-btn bw-btn-ghost" onClick={() => openEnquiry("")}>Contact Us</button>
                                {brochureUrl && (
                                    <a className="bw-btn bw-btn-ghost" href={brochureUrl} target="_blank" rel="noopener noreferrer" download>
                                        <i className="bi bi-download" aria-hidden="true" /> Download Portfolio / Catalog
                                    </a>
                                )}
                            </div>
                           
                        </div>

                        {/* signature floating trust card */}
                        {(years !== null || products.length > 0) && (
                            <div className="bw-trustcard">
                                <div className="bw-trustcard-glow" aria-hidden="true"></div>
                                <div className="bw-trustcard-row">
                                    <Stars n={d.rating !== null ? Math.round(d.rating) : 5} />
                                    <span className="bw-trustcard-label">{d.is_verified ? "Verified listing" : "Business listing"}</span>
                                </div>
                                <div className="bw-trustcard-stats">
                                    {years !== null && <div><b>{years}+</b><span>Years</span></div>}
                                    {products.length > 0 && <div><b>{products.length}+</b><span>Products</span></div>}
                                    <div><b><i className={`bi ${d.gst_no ? "bi-patch-check-fill" : "bi-receipt"}`} aria-hidden="true" /></b><span>VAT/PAN</span></div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="bw-hero-wave" aria-hidden="true">
                        <svg viewBox="0 0 1440 60" preserveAspectRatio="none"><path d="M0,32 C240,60 480,0 720,18 C960,36 1200,54 1440,26 L1440,60 L0,60 Z"></path></svg>
                    </div>
                </section>

                {isSiteEmpty && (
                    <section className="bw-wrap">
                        <SiteEmptyState
                            icon="bi-shop"
                            title="This business page is still being set up"
                            message="Once the owner adds company details, products and photos, they'll appear here."
                        />
                    </section>
                )}

                 {/* ABOUT */}
                {d.about && (
                    <section id="about" className="bw-wrap">
                        <div className="bw-card">
                            <h2 className="bw-h">About Us</h2>
                            <p className="bw-desc">{d.about}</p>
                        </div>
                    </section>
                )}

                {/* WHY CHOOSE US */}
                <section className="bw-wrap">
                    <div className="bw-h-row"><h2 className="bw-h">Why Choose Us</h2></div>
                    <div className="bw-why">
                        {whyUs.map(([ic, t, ds], i) => (
                            <div className="bw-whycard" key={i}>
                                <div className="bw-why-ic"><i className={`bi ${ic}`} aria-hidden="true" /></div>
                                <h3>{t}</h3>
                                <p>{ds}</p>
                            </div>
                        ))}
                    </div>
                </section>

                

                {/* FACTSHEET BAND */}
                {facts.length > 0 && (
                    <section className="bw-wrap">
                        <div className="bw-factband">
                            {facts.map(([k, v, ic], i) => (
                                <div className="bw-fact" key={i}>
                                    <div className="bw-fact-ic"><i className={`bi ${ic}`} aria-hidden="true" /></div>
                                    <div><div className="bw-fact-k">{k}</div><div className="bw-fact-v">{v}</div></div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* CATEGORY RANGE GRID */}
                {products.length > 0 && (
                    <section id="products" className="bw-wrap">
                        <div className="bw-h-row">
                            <h2 className="bw-h">Products &amp; Services</h2>
                            <button type="button" className="bw-viewall" onClick={() => openEnquiry("")}>Get Best Quote →</button>
                        </div>
                        {isSearching && (
                            <p className="bw-search-status">
                                {hasSearchResults
                                    ? `${filteredProducts.length} result${filteredProducts.length === 1 ? "" : "s"} for "${searchQuery.trim()}"`
                                    : `No results for "${searchQuery.trim()}"`}
                                <button type="button" className="bw-btnreset bw-search-status-clear" onClick={clearSearch}>Clear search</button>
                            </p>
                        )}
                        {hasSearchResults ? (
                            <>
                                <div className="bw-range">
                                    {categories.map((c) => {
                                        const list = grouped[c]; const first = list[0];
                                        return (
                                            <article className="bw-range-card" key={c}>
                                                <button type="button" className="bw-range-img bw-btnreset" onClick={() => openProduct(first)} aria-label={`View ${c} details`}>
                                                    <BwImg src={resolveImg(first?.image)} alt={c} icon="bi-box-seam" />
                                                </button>
                                                <div className="bw-range-body">
                                                    <h3 className="bw-range-title">{c}</h3>
                                                    <ul className="bw-range-list">
                                                        {list.slice(0, 3).map((p, i) => (
                                                            <li key={i}>
                                                                <button type="button" className="bw-btnreset bw-range-listbtn" onClick={() => openProduct(p)}>{p.name}</button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <button type="button" className="bw-btnreset bw-range-view" onClick={() => openProduct(first)}>View Details ❯</button>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                                <div className="bw-center-btn"><button type="button" className="bw-btn bw-btn-accent" style={{ width: "auto", padding: "11px 26px" }} onClick={() => openEnquiry("")}>View Complete Range</button></div>
                            </>
                        ) : (
                            <SiteEmptyState icon="bi-search" title="No matching products or services" message="Try a different search term, or clear the search to see the full range." />
                        )}
                    </section>
                )}
                {products.length === 0 && !isSiteEmpty && (
                    <section id="products" className="bw-wrap">
                        <h2 className="bw-h">Products &amp; Services</h2>
                        <SiteEmptyState icon="bi-box-seam" title="No products listed yet" message="Products added by the owner will show up here." />
                    </section>
                )}

                {/* PRODUCT VIDEO */}
                {videos.length > 0 && (
                    <section id="videos" className="bw-wrap">
                        <h2 className="bw-h">Product Video</h2>
                        <div className="bw-videos">
                            {videos.map((v, i) => (
                                <div className="bw-video" key={i}>
                                    <button type="button" className="bw-video-thumb" onClick={() => setVideo(v.youtubeId)}>
                                        <img src={`https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg`} alt={v.title} loading="lazy" />
                                        <span className="bw-play" aria-hidden="true"><i className="bi bi-play-fill" /></span>
                                    </button>
                                    <p className="bw-video-t">{v.title}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* PHOTOS */}
                {gallery.length > 0 && (
                    <section id="photos" className="bw-wrap">
                        <h2 className="bw-h">Photos &amp; Gallery</h2>
                        <div className="bw-gallery">
                            {gallery.map((g, i) => (
                                <div key={i} className="bw-gimg">
                                    <BwImg src={resolveImg(g)} alt={`${d.name || "Business"} photo ${i + 1}`} icon="bi-images" />
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                   
                {/* REVIEWS */}
                {REVIEWS.length > 0 && (
                    <section className="bw-wrap">
                        <h2 className="bw-h">Customer Reviews</h2>
                        <div className="bw-reviews">
                            {REVIEWS.map((r, i) => (
                                <div className="bw-review" key={i}>
                                    <i className="bi bi-quote bw-review-quote" aria-hidden="true" />
                                    <p>{r.text}</p>
                                    <div className="bw-review-top">
                                        <div className="bw-avatar" aria-hidden="true">{r.name[0]}</div>
                                        <div><div className="bw-review-name">{r.name}</div><Stars n={r.rating} /></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* FAQ */}
                <section className="bw-wrap">
                    <h2 className="bw-h">Frequently Asked Questions</h2>
                    <div className="bw-faq">
                        {FAQS.map((f, i) => {
                            const isOpen = openFaq === i;
                            return (
                                <div className={`bw-faq-item ${isOpen ? "open" : ""}`} key={i}>
                                    <button
                                        type="button"
                                        className="bw-faq-q"
                                        aria-expanded={isOpen}
                                        aria-controls={`bw-faq-a-${i}`}
                                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                                    >
                                        <span>{f.q}</span>
                                        <i className="bi bi-chevron-down" aria-hidden="true" />
                                    </button>
                                    {isOpen && <p className="bw-faq-a" id={`bw-faq-a-${i}`}>{f.a}</p>}
                                </div>
                            );
                        })}
                    </div>
                </section>
            </main>

            {/* FOOTER */}
            <footer id="contact" className="bw-footer">
                <div className="bw-footer-banner"><h2>Get in touch with us</h2></div>
                <div className="bw-footer-body">
                    <div className="bw-footer-col">
                        <h3>Our Company</h3>
                        <ul>
                            {d.about && <li><button type="button" className="bw-btnreset" onClick={() => scrollTo("about", "about")}>About Us</button></li>}
                            {products.length > 0 && <li><button type="button" className="bw-btnreset" onClick={() => scrollTo("products", "products")}>Products &amp; Services</button></li>}
                            {videos.length > 0 && <li><button type="button" className="bw-btnreset" onClick={() => scrollTo("videos", "videos")}>Videos</button></li>}
                            {gallery.length > 0 && <li><button type="button" className="bw-btnreset" onClick={() => scrollTo("photos", "photos")}>Photos</button></li>}
                        </ul>
                    </div>
                    <div className="bw-footer-col">
                        <h3>Reach Us</h3>
                        <p className="bw-footer-name"><i className="bi bi-geo-alt-fill" aria-hidden="true" /> {d.name || "Your Business Name"}</p>
                        {d.address && <p>{d.address}</p>}
                        {d.contact_person && <p><i className="bi bi-person-fill" aria-hidden="true" /> {d.contact_person}</p>}
                        {d.phone && <p><i className="bi bi-telephone-fill" aria-hidden="true" /> {d.phone}</p>}
                        {d.email && <p><i className="bi bi-envelope-fill" aria-hidden="true" /> {d.email}</p>}
                        <div className="bw-footer-cta">
                            <button type="button" className="bw-btn bw-btn-primary" onClick={() => openEnquiry("")}><i className="bi bi-send-fill" aria-hidden="true" /> Send Enquiry</button>
                            <a className="bw-btn bw-btn-outline bw-btn-dark" href={d.phone ? `tel:${d.phone}` : "#"}><i className="bi bi-telephone-fill" aria-hidden="true" /> Call Now</a>
                        </div>
                    </div>
                    <div className="bw-footer-col">
                        <h3>Connect</h3>
                        <div className="bw-footer-social">
                            {d.website && <a href={d.website} target="_blank" rel="noreferrer"><i className="bi bi-globe2" aria-hidden="true" /> Website</a>}
                            {d.linkfacebook && <a href={d.linkfacebook} target="_blank" rel="noreferrer"><i className="bi bi-facebook" aria-hidden="true" /> Facebook</a>}
                            {d.linkinstagram && <a href={d.linkinstagram} target="_blank" rel="noreferrer"><i className="bi bi-instagram" aria-hidden="true" /> Instagram</a>}
                            {d.linklinkedin && <a href={d.linklinkedin} target="_blank" rel="noreferrer"><i className="bi bi-linkedin" aria-hidden="true" /> LinkedIn</a>}
                        </div>
                    </div>
                </div>
                <div className="bw-footer-bottom">© {new Date().getFullYear()} {d.name || "Your Business"}. All Rights Reserved. &nbsp;·&nbsp; Powered by <b>Dial Nepal</b></div>
            </footer>

            {/* PRODUCT DETAIL POPUP */}
            {detail && (
                <div className="bw-overlay" role="presentation" onMouseDown={(e) => e.target.classList.contains("bw-overlay") && closeDetail()}>
                    <div className="bw-pd" role="dialog" aria-modal="true" aria-label={detail.name || "Product details"}>
                        <button type="button" className="bw-close bw-pd-close" onClick={closeDetail} aria-label="Close product details">×</button>
                        <div className="bw-pd-grid">
                            <div className="bw-pd-img">
                                <BwImg src={resolveImg(detail.image)} alt={detail.name} icon="bi-box-seam" />
                            </div>
                            <div className="bw-pd-info">
                                {detail.category && <span className="bw-pd-cat">{detail.category}</span>}
                                <h2 className="bw-pd-name">{detail.name}</h2>
                                <div className="bw-pd-price">
                                    {detail.price ? <>₹ {detail.price}<small> / {detail.unit || "Unit"}</small></> : <em>Ask Price</em>}
                                </div>
                                {detail.description && (
                                    <div className="bw-pd-sec">
                                        <h4>Product Details</h4>
                                        <p>{detail.description}</p>
                                    </div>
                                )}
                                <table className="bw-pd-specs">
                                    <tbody>
                                        {detail.category && <tr><th>Category</th><td>{detail.category}</td></tr>}
                                        {detail.unit && <tr><th>Unit</th><td>{detail.unit}</td></tr>}
                                        {detail.price && <tr><th>Price</th><td>₹ {detail.price}</td></tr>}
                                        <tr><th>Availability</th><td>In Stock</td></tr>
                                    </tbody>
                                </table>
                                <div className="bw-pd-cta">
                                    <button type="button" className="bw-btn bw-btn-primary" onClick={() => { const n = detail.name; closeDetail(); openEnquiry(n); }}><i className="bi bi-send-fill" aria-hidden="true" /> Send Enquiry</button>
                                    <a className="bw-btn bw-btn-outline" href={d.phone ? `tel:${d.phone}` : "#"}><i className="bi bi-telephone-fill" aria-hidden="true" /> Call Now</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ENQUIRY MODAL */}
            {enq.open && (
                <div className="bw-overlay" role="presentation" onMouseDown={(e) => e.target.classList.contains("bw-overlay") && closeEnquiry()}>
                    <div className="bw-dialog" role="dialog" aria-modal="true" aria-label="Send enquiry">
                        <div className="bw-dialog-head">
                            <b>Send Enquiry {enq.product ? `— ${enq.product}` : ""}</b>
                            <button type="button" className="bw-close" onClick={closeEnquiry} aria-label="Close enquiry form">×</button>
                        </div>
                        <form onSubmit={submitEnquiry} className="bw-dialog-body">
                            {enqMsg && <div className={`bw-alert ${enqMsg.type}`} role="alert">{enqMsg.text}</div>}
                            <label className="visually-hidden-bw" htmlFor="bw-enq-name">Your Name</label>
                            <input id="bw-enq-name" placeholder="Your Name *" value={enq.name} onChange={(e) => setEnq((s) => ({ ...s, name: e.target.value }))} required />
                            <label className="visually-hidden-bw" htmlFor="bw-enq-mobile">Mobile Number</label>
                            <input id="bw-enq-mobile" placeholder="Mobile Number *" value={enq.mobile} onChange={(e) => setEnq((s) => ({ ...s, mobile: e.target.value }))} required />
                            <label className="visually-hidden-bw" htmlFor="bw-enq-email">Email</label>
                            <input id="bw-enq-email" placeholder="Email" value={enq.email} onChange={(e) => setEnq((s) => ({ ...s, email: e.target.value }))} />
                            <label className="visually-hidden-bw" htmlFor="bw-enq-msg">Your requirement</label>
                            <textarea id="bw-enq-msg" rows="3" placeholder="Your requirement" value={enq.message} onChange={(e) => setEnq((s) => ({ ...s, message: e.target.value }))} />
                            <button type="submit" className="bw-btn bw-btn-primary" style={{ width: "100%" }}>Submit Enquiry</button>
                        </form>
                    </div>
                </div>
            )}

            {/* VIDEO MODAL */}
            {video && (
                <div className="bw-overlay" role="presentation" onMouseDown={(e) => e.target.classList.contains("bw-overlay") && closeVideo()}>
                    <div className="bw-vdialog" role="dialog" aria-modal="true" aria-label="Product video">
                        <button type="button" className="bw-close bw-vclose" onClick={closeVideo} aria-label="Close video">×</button>
                        <div className="bw-vframe"><iframe src={`https://www.youtube.com/embed/${video}?autoplay=1`} title="Product video" allow="autoplay; encrypted-media" allowFullScreen /></div>
                    </div>
                </div>
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&display=swap');
                .bw{
                    --ink:#1b1e24; --muted:#6b7280; --paper:#f6f5f1; --panel:#ffffff; --border:#e5e3da;
                    --brand:#1f2a3a; --brand-2:#3a4a63; --accent:#b8873a; --accent-d:#966d2c; --accent-soft:#f6ede0;
                    --success:#1f6b45; --success-tint:#e5f0e9;
                    font-family:'Inter',system-ui,Arial,sans-serif; background:var(--paper); color:var(--ink); min-height:100vh;
                }
                .bw *{box-sizing:border-box;} .bw a{text-decoration:none;color:inherit;} .bw img{max-width:100%;display:block;}
                .bw h1,.bw h2,.bw h3{font-family:'Fraunces',serif; letter-spacing:-.01em;}
                .bw *:focus-visible{ outline:3px solid #97a7bf; outline-offset:2px; border-radius:4px; }
                .bw a, .bw button{ transition:all .2s ease; }

                .bw-skip-link{ position:absolute; left:-9999px; top:auto; z-index:2000; background:var(--brand); color:#fff; padding:10px 18px; border-radius:0 0 8px 0; }
                .bw-skip-link:focus{ left:0; top:0; }
                .visually-hidden-bw{ position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
                .bw-btnreset{ background:none; border:none; padding:0; font:inherit; color:inherit; cursor:pointer; text-align:left; }

                /* Gradient fallback tile — themed, used whenever an image is missing or fails to load */
                .bw-img-fallback{
                    width:100%; height:100%; min-height:120px; display:flex; align-items:center; justify-content:center;
                    background:linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 55%, #10161f 100%);
                    background-size:200% 200%; animation:bw-fallback-shift 6s ease infinite;
                    color:var(--accent); font-size:2.2rem; position:relative; overflow:hidden;
                }
                .bw-img-fallback:before{ content:""; position:absolute; inset:0; background-image:radial-gradient(rgba(184,135,58,.2) 1.5px, transparent 1.5px); background-size:16px 16px; }
                .bw-img-fallback i{ position:relative; z-index:1; opacity:.9; }
                @keyframes bw-fallback-shift{ 0%{ background-position:0% 50%; } 50%{ background-position:100% 50%; } 100%{ background-position:0% 50%; } }

                .bw-center{display:flex;align-items:center;justify-content:center;height:100vh;background:var(--paper);}
                .bw-wrap{
                    width:100%;
                    margin:56px 0 0;
                    padding:0 48px;
                }
                .bw-h{font-size:26px;font-weight:600;margin:0 0 22px;position:relative;padding-left:16px;}
                .bw-h:before{content:"";position:absolute;left:0;top:4px;bottom:4px;width:4px;border-radius:3px;background:var(--accent);}
                .bw-h-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;flex-wrap:wrap;gap:8px;} .bw-viewall{color:var(--brand-2);font-weight:700;font-size:13px;cursor:pointer;background:none;border:none;padding:0;position:relative;}
                .bw-viewall:after{ content:""; position:absolute; left:0; right:0; bottom:-3px; height:1.5px; background:var(--brand-2); transform:scaleX(0); transform-origin:right; transition:transform .2s ease; }
                .bw-viewall:hover:after{ transform:scaleX(1); transform-origin:left; }
                .bw-card{background:var(--panel);border:1px solid var(--border);border-radius:14px;padding:26px 28px;transition:box-shadow .25s ease;}
                .bw-card:hover{ box-shadow:0 12px 28px rgba(31,42,58,.08); }
                .bw-desc{font-size:15px;line-height:1.8;color:#3c3b34;white-space:pre-line;margin:0;}

                .bw-head{background:var(--panel);border-bottom:1px solid var(--border);}
                .bw-head-inner{
                    width:100%;
                    padding:18px 48px;
                    display:flex;align-items:center;gap:18px;flex-wrap:wrap;
                }
                .bw-logoimg,.bw-logobox{width:72px;height:72px;border-radius:12px;flex-shrink:0;object-fit:cover;}
                .bw-logobox{background:linear-gradient(135deg,var(--brand-2),var(--brand));display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:24px;font-family:'Fraunces';}
                .bw-head-main{flex:1;min-width:240px;} .bw-cname{font-size:25px;font-weight:600;margin:0 0 8px;}
                .bw-badges{display:flex;flex-wrap:wrap;gap:8px 10px;font-size:12.5px;color:var(--muted);align-items:center;}
                .bw-loc{font-weight:500;color:#4a493f;display:inline-flex;align-items:center;gap:5px;}
                .bw-chip{background:#f1efe8;padding:4px 11px;border-radius:20px;font-weight:600;display:inline-flex;align-items:center;gap:5px;transition:transform .2s ease;}
                .bw-chip:hover{ transform:translateY(-2px); }
                .bw-chip.ok{background:var(--success-tint);color:var(--success);}
                .bw-stars{color:var(--accent);letter-spacing:1px;}
                .bw-head-cta{display:flex;gap:10px;flex-wrap:wrap;}
                .bw-btn{padding:11px 20px;border-radius:9px;font-size:14px;font-weight:600;cursor:pointer;border:none;text-align:center;transition:all .2s ease;font-family:'Inter';display:inline-flex;align-items:center;justify-content:center;gap:7px;}
                .bw-btn-primary{background:var(--brand);color:#fff;} .bw-btn-primary:hover{background:var(--brand-2);transform:translateY(-2px);box-shadow:0 12px 22px rgba(31,42,58,.28);}
                .bw-btn-outline{background:#fff;color:var(--brand);border:1.5px solid var(--brand);} .bw-btn-outline:hover{background:#f1f5f2;transform:translateY(-2px);}
                .bw-btn-dark{background:transparent;color:#fff;border-color:rgba(255,255,255,.5);margin-top:10px;} .bw-btn-dark:hover{background:rgba(255,255,255,.1);transform:translateY(-2px);}
                .bw-btn-accent{background:var(--accent);color:#fff;} .bw-btn-accent:hover{background:var(--accent-d);transform:translateY(-2px);box-shadow:0 12px 22px rgba(184,135,58,.32);}
                .bw-btn-ghost{background:rgba(255,255,255,.1);color:#fff;border:1.5px solid rgba(255,255,255,.55);} .bw-btn-ghost:hover{background:rgba(255,255,255,.18);transform:translateY(-2px);}

                .bw-nav{background:var(--panel);border-bottom:1px solid var(--border);position:sticky;top:0;z-index:20;transition:box-shadow .25s ease;}
                .bw-nav.bw-nav-scrolled{ box-shadow:0 8px 22px rgba(31,42,58,.08); }
                .bw-nav-inner{
                    width:100%;
                    padding:0 48px;
                    display:flex;align-items:center;gap:2px;overflow-x:auto;
                }
                .bw-navtab{padding:15px 18px;font-size:14px;font-weight:500;color:var(--muted);cursor:pointer;white-space:nowrap;border-bottom:2px solid transparent;transition:.15s;background:none;border-top:none;border-left:none;border-right:none;font-family:inherit;}
                .bw-navtab:hover{color:var(--brand);} .bw-navtab.active{color:var(--brand);border-bottom-color:var(--accent);font-weight:600;}
                .bw-nav-search{margin-left:auto;position:relative;display:flex;align-items:center;}
                .bw-nav-search i.bi-search{position:absolute;left:13px;color:var(--muted);font-size:13px;pointer-events:none;}
                .bw-nav-search input{padding:9px 34px 9px 32px;border:1px solid var(--border);border-radius:22px;font-size:13px;min-width:230px;background:var(--paper);transition:border-color .2s ease, box-shadow .2s ease;}
                .bw-nav-search input:focus{ outline:none; border-color:var(--brand-2); box-shadow:0 0 0 3px rgba(58,74,99,.12); }
                .bw-nav-search input[type="search"]::-webkit-search-cancel-button{ -webkit-appearance:none; }
                .bw-search-clear{position:absolute;right:8px;width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:var(--border);color:var(--ink);font-size:10px;cursor:pointer;border:none;padding:0;}
                .bw-search-clear:hover{ background:var(--brand-2); color:#fff; }
                @media(max-width:600px){ .bw-nav-search input{ min-width:150px; } }
                .bw-search-status{ display:flex; align-items:center; gap:12px; flex-wrap:wrap; font-size:13.5px; color:var(--muted); margin:-6px 0 20px; }
                .bw-search-status-clear{ color:var(--brand-2); font-weight:700; }
                .bw-search-status-clear:hover{ text-decoration:underline; }

                .bw-hero{background-size:cover;background-position:center;color:#fff;position:relative;overflow:hidden;}
                .bw-hero-fallback{background:linear-gradient(120deg,var(--brand),var(--brand-2));}
                .bw-hero-fallback:before{content:"";position:absolute;inset:0;background-image:radial-gradient(rgba(255,255,255,.08) 1.5px, transparent 1.5px);background-size:22px 22px;}
                .bw-hero-watermark{position:absolute;right:-40px;top:50%;transform:translateY(-50%);font-family:'Fraunces';font-size:280px;font-weight:600;color:rgba(255,255,255,.06);line-height:1;pointer-events:none;user-select:none;}
                .bw-hero-blob{ position:absolute; border-radius:50%; filter:blur(70px); pointer-events:none; z-index:0; }
                .bw-hero-blob-1{ width:360px; height:360px; background:var(--accent); opacity:.22; top:-140px; right:8%; }
                .bw-hero-blob-2{ width:260px; height:260px; background:#fff; opacity:.06; bottom:-120px; left:4%; }
                .bw-hero-in{
                    position:relative;
                    width:100%;
                    margin:0;
                    padding:88px 48px 110px;
                    display:flex;align-items:flex-end;justify-content:space-between;gap:40px;flex-wrap:wrap;
                    z-index:1;
                }
                .bw-hero-text{max-width:600px;}
                .bw-hero-eyebrow{color:var(--accent);font-weight:600;letter-spacing:1.5px;font-size:13px;text-transform:uppercase;}
                .bw-hero-title{font-size:46px;font-weight:600;margin:14px 0 16px;line-height:1.1;}
                @media(max-width:640px){.bw-hero-title{font-size:32px;} .bw-hero-in{padding:56px 16px 70px;}}
                .bw-hero-desc{font-size:15.5px;line-height:1.75;opacity:.88;margin:0 0 30px;}
                .bw-hero-cta{display:flex;gap:14px;flex-wrap:wrap;}
                .bw-trustcard{background:#fff;color:var(--ink);border-radius:14px;padding:20px 22px;min-width:230px;box-shadow:0 20px 44px rgba(0,0,0,.28);transition:transform .3s ease;}
                .bw-trustcard:hover{ transform:translateY(-4px); }
                .bw-trustcard-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;}
                .bw-trustcard-label{font-size:11.5px;color:var(--muted);font-weight:600;}
                .bw-trustcard-stats{display:flex;gap:18px;border-top:1px solid var(--border);padding-top:12px;}
                .bw-trustcard-stats b{display:block;font-family:'Fraunces';font-size:19px;color:var(--brand);}
                .bw-trustcard-stats span{font-size:11px;color:var(--muted);font-weight:600;}

                .bw-why{display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:18px;}
                .bw-whycard{background:var(--panel);border:1px solid var(--border);border-radius:14px;padding:26px 22px;transition:transform .25s ease, box-shadow .25s ease, border-color .25s ease;}
                .bw-whycard:hover{transform:translateY(-6px);border-color:var(--brand-2);box-shadow:0 16px 30px rgba(31,42,58,.1);}
                .bw-why-ic{width:52px;height:52px;border-radius:12px;background:#eef1f5;color:var(--brand-2);display:flex;align-items:center;justify-content:center;font-size:22px;margin-bottom:14px;transition:transform .25s ease, background .25s ease, color .25s ease;}
                .bw-whycard:hover .bw-why-ic{ background:var(--brand-2); color:#fff; transform:scale(1.08) rotate(-4deg); }
                .bw-whycard h3{font-size:16px;margin:0 0 6px;font-weight:600;} .bw-whycard p{font-size:13.5px;color:var(--muted);margin:0;line-height:1.6;}

                .bw-factband{background:var(--panel);border:1px solid var(--border);border-radius:14px;padding:8px;display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));}
                .bw-fact{display:flex;gap:13px;align-items:center;padding:16px 18px;border-radius:10px;transition:background .2s ease;}
                .bw-fact:hover{ background:var(--accent-soft); }
                .bw-fact-ic{width:38px;height:38px;border-radius:10px;background:#f1efe8;color:var(--accent-d);display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0;}
                .bw-fact-k{font-size:11.5px;color:var(--muted);} .bw-fact-v{font-size:14px;font-weight:600;}

                .bw-range{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:20px;margin-top:4px;}
                .bw-range-card{background:var(--panel);border:1px solid var(--border);border-radius:14px;overflow:hidden;transition:box-shadow .25s ease, transform .25s ease;}
                .bw-range-card:hover{box-shadow:0 18px 34px rgba(31,42,58,.12);transform:translateY(-4px);}
                .bw-range-img{width:100%;height:190px;background:#f1efe8;display:flex;align-items:center;justify-content:center;color:var(--muted);cursor:pointer;overflow:hidden;}
                .bw-range-img img{width:100%;height:100%;object-fit:cover;transition:.3s;} .bw-range-card:hover .bw-range-img img{transform:scale(1.06);}
                .bw-range-card:hover .bw-img-fallback{ transform:scale(1.06); }
                .bw-range-body{padding:16px 18px;} .bw-range-title{font-size:16px;margin:0 0 8px;font-weight:600;}
                .bw-range-list{list-style:none;padding:0;margin:0 0 10px;} .bw-range-list li{padding:0;border-bottom:1px dashed var(--border);}
                .bw-range-listbtn{width:100%;font-size:12.5px;color:#4a493f;padding:4px 0;}
                .bw-range-listbtn:hover{color:var(--brand-2);padding-left:3px;}
                .bw-range-view{color:var(--brand-2);font-size:12.5px;font-weight:700;cursor:pointer;}
                .bw-range-view:hover{ letter-spacing:.3px; }
                .bw-center-btn{text-align:center;margin-top:26px;}

                .bw-videos{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;}
                .bw-video-thumb{position:relative;width:100%;border:none;padding:0;border-radius:12px;overflow:hidden;cursor:pointer;display:block;}
                .bw-video-thumb img{width:100%;height:150px;object-fit:cover;transition:transform .35s ease;}
                .bw-video-thumb:hover img{ transform:scale(1.07); }
                .bw-play{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:22px;color:#fff;background:rgba(15,26,20,.4);transition:background .25s ease;}
                .bw-video-thumb:hover .bw-play{ background:rgba(15,26,20,.55); }
                .bw-video-t{font-size:13px;font-weight:600;margin:8px 0 0;}

                .bw-gallery{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:14px;}
                .bw-gimg{border-radius:12px;overflow:hidden;height:170px;} .bw-gimg img{width:100%;height:100%;object-fit:cover;transition:.3s;} .bw-gimg:hover img{transform:scale(1.06);}
                .bw-gimg:hover .bw-img-fallback{ transform:scale(1.06); }

                .bw-reviews{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:18px;}
                .bw-review{border:1px solid var(--border);border-radius:14px;padding:22px;background:var(--panel);position:relative;transition:transform .25s ease, box-shadow .25s ease;}
                .bw-review:hover{ transform:translateY(-4px); box-shadow:0 16px 30px rgba(31,42,58,.1); }
                .bw-review-quote{position:absolute;top:16px;right:18px;font-size:28px;color:var(--border);}
                .bw-review p{font-size:14px;color:#3c3b34;margin:0 0 16px;line-height:1.65;}
                .bw-review-top{display:flex;align-items:center;gap:10px;}
                .bw-avatar{width:38px;height:38px;border-radius:50%;background:var(--brand);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;}
                .bw-review-name{font-weight:600;font-size:13.5px;}

                .bw-faq{max-width:760px;}
                .bw-faq-item{border-bottom:1px solid var(--border);}
                .bw-faq-q{width:100%;background:none;border:none;padding:18px 4px;display:flex;justify-content:space-between;align-items:center;cursor:pointer;font-size:15px;font-weight:600;text-align:left;color:var(--ink);font-family:inherit;}
                .bw-faq-q:hover{ color:var(--brand-2); }
                .bw-faq-q i{transition:.2s;color:var(--muted);}
                .bw-faq-item.open .bw-faq-q i{transform:rotate(180deg);color:var(--brand-2);}
                .bw-faq-a{margin:0 4px 18px;font-size:14px;color:var(--muted);line-height:1.7;animation:bw-faq-in .2s ease;}
                @keyframes bw-faq-in{ from{opacity:0; transform:translateY(-4px);} to{opacity:1; transform:none;} }

                .bw-footer{margin-top:60px;background:var(--brand);color:#cfd8d1;}
                .bw-footer-banner{background:var(--ink);text-align:center;padding:28px 16px;} .bw-footer-banner h2{color:#fff;margin:0;font-size:23px;font-weight:600;}
                .bw-footer-body{
                    width:100%;
                    margin:0;
                    padding:34px 48px;
                    display:grid;grid-template-columns:1fr 1.3fr 1fr;gap:24px;
                }
                @media(max-width:760px){.bw-footer-body{grid-template-columns:1fr;}}
                .bw-footer-col h3{color:#fff;font-size:15px;margin:0 0 14px;font-weight:600;} .bw-footer-col ul{list-style:none;padding:0;margin:0;} .bw-footer-col li{padding:5px 0;font-size:13px;} .bw-footer-col li button{color:#cfd8d1;} .bw-footer-col li button:hover{color:var(--accent);padding-left:3px;}
                .bw-footer-col p{font-size:13px;margin:5px 0;display:flex;gap:8px;align-items:center;} .bw-footer-name{font-weight:700;color:#fff;}
                .bw-footer-cta{margin-top:14px;display:flex;flex-wrap:wrap;gap:10px;} .bw-footer-social{display:flex;flex-direction:column;gap:8px;}
                .bw-footer-social a{background:rgba(255,255,255,.08);padding:8px 12px;border-radius:8px;font-size:13px;font-weight:600;display:flex;align-items:center;gap:8px;}
                .bw-footer-social a:hover{ background:var(--accent); color:#fff; transform:translateX(3px); }
                .bw-footer-bottom{border-top:1px solid rgba(255,255,255,.14);text-align:center;padding:16px;font-size:12px;} .bw-footer-bottom b{color:var(--accent);}

                .bw-overlay{position:fixed;inset:0;background:rgba(15,23,42,.6);z-index:20000;display:flex;align-items:center;justify-content:center;padding:24px 12px;overflow-y:auto;animation:bw-fade-in .15s ease;}
                @keyframes bw-fade-in{ from{opacity:0;} to{opacity:1;} }
                .bw-dialog{background:#fff;width:100%;max-width:470px;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.35);overflow:hidden;align-self:flex-start;margin-top:44px;animation:bw-pop-in .2s cubic-bezier(.16,1,.3,1);}
                @keyframes bw-pop-in{ from{opacity:0; transform:translateY(10px) scale(.98);} to{opacity:1; transform:none;} }
                .bw-dialog-head{display:flex;justify-content:space-between;align-items:center;padding:16px 18px;border-bottom:1px solid var(--border);background:#f6f5f1;}
                .bw-close{cursor:pointer;font-size:24px;line-height:1;color:#94a3b8;background:none;border:none;padding:4px 8px;border-radius:8px;}
                .bw-close:hover{color:#334155;background:rgba(0,0,0,.05);}
                .bw-dialog-body{padding:18px;display:flex;flex-direction:column;gap:11px;}
                .bw-dialog-body input,.bw-dialog-body textarea{padding:11px 13px;border:1px solid var(--border);border-radius:9px;font-size:14px;width:100%;font-family:'Inter';transition:border-color .2s ease, box-shadow .2s ease;}
                .bw-dialog-body input:focus,.bw-dialog-body textarea:focus{outline:none;border-color:var(--brand-2);box-shadow:0 0 0 3px rgba(58,74,99,.15);}
                .bw-alert{padding:9px 12px;border-radius:8px;font-size:13px;} .bw-alert.success{background:#e5f0e9;color:#155f3c;} .bw-alert.danger{background:#fdecec;color:#c0392b;}
                .bw-vdialog{width:100%;max-width:860px;position:relative;} .bw-vframe{position:relative;padding-top:56.25%;border-radius:12px;overflow:hidden;background:#000;} .bw-vframe iframe{position:absolute;inset:0;width:100%;height:100%;border:0;}
                .bw-vclose{position:absolute;top:-40px;right:0;color:#fff;font-size:26px;}

                .bw-pd{background:#fff;width:100%;max-width:860px;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.35);position:relative;align-self:flex-start;margin-top:40px;overflow:hidden;animation:bw-pop-in .2s cubic-bezier(.16,1,.3,1);}
                .bw-pd-close{position:absolute;top:10px;right:16px;z-index:2;}
                .bw-pd-grid{display:grid;grid-template-columns:1fr 1.2fr;gap:0;}
                @media(max-width:700px){.bw-pd-grid{grid-template-columns:1fr;}}
                .bw-pd-img{background:#f1efe8;display:flex;align-items:center;justify-content:center;min-height:320px;padding:16px;}
                .bw-pd-img img{max-width:100%;max-height:420px;object-fit:contain;border-radius:8px;}
                .bw-pd-img .bw-img-fallback{ min-height:288px; border-radius:8px; }
                .bw-pd-info{padding:26px 26px 22px;}
                .bw-pd-cat{display:inline-block;background:#eef1f5;color:var(--brand-2);font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;margin-bottom:8px;}
                .bw-pd-name{font-size:22px;margin:0 0 8px;font-weight:600;}
                .bw-pd-price{font-size:22px;color:var(--brand);font-weight:700;margin-bottom:16px;} .bw-pd-price small{color:var(--muted);font-weight:500;font-size:14px;}
                .bw-pd-sec{margin-bottom:14px;} .bw-pd-sec h4{font-size:14px;margin:0 0 6px;} .bw-pd-sec p{font-size:13.5px;color:#374151;line-height:1.6;margin:0;white-space:pre-line;}
                .bw-pd-specs{width:100%;border-collapse:collapse;margin-bottom:18px;font-size:13px;}
                .bw-pd-specs th{text-align:left;color:var(--muted);font-weight:500;padding:7px 10px;width:40%;background:#f6f5f1;border:1px solid var(--border);}
                .bw-pd-specs td{padding:7px 10px;font-weight:600;border:1px solid var(--border);}
                .bw-pd-cta{display:flex;gap:10px;flex-wrap:wrap;} .bw-pd-cta .bw-btn{flex:1;min-width:140px;}

                @media (prefers-reduced-motion: reduce){
                    .bw-overlay,.bw-dialog,.bw-pd,.bw-faq-a,.bw-img-fallback{ animation:none !important; }
                }
            `}</style>
        </div>
    );
}